import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  const [currentTrack, setCurrentTrack] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (location.state?.scrollTo) {
      const element = document.getElementById(location.state.scrollTo);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('submitting');

    const form = e.currentTarget;
    const formData = new FormData(form);
    
    try {
      const response = await fetch('https://readdy.ai/api/form/d5hj9ob48noljcu3pqug', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formData as any).toString(),
      });

      if (response.ok) {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 3000);
      } else {
        setFormStatus('error');
        setTimeout(() => setFormStatus('idle'), 3000);
      }
    } catch (error) {
      setFormStatus('error');
      setTimeout(() => setFormStatus('idle'), 3000);
    }
  };

  const videos = [
    {
      id: 1,
      title: 'Concrete Resonance',
      duration: '4:32',
      thumbnail: 'https://public.readdy.ai/ai/img_res/5382b0cdfdf65cdd8dcffaef0fdad7ea.jpg',
      description: 'コンクリート彫刻と音響の融合による実験的映像作品。素材の持つ質感と音の振動が生み出す新しい表現を探求しています。'
    },
    {
      id: 2,
      title: 'Wooden Echoes',
      duration: '3:48',
      thumbnail: 'https://readdy.ai/api/search-image?query=wooden%20abstract%20sculpture%20with%20natural%20grain%20texture%20in%20dark%20studio%20setting%20artistic%20lighting%20contemporary%20minimalist%20aesthetic%20organic%20forms&width=1200&height=675&seq=video2&orientation=landscape',
      description: '木材の有機的な形態と音楽が織りなす視覚的詩。自然素材の温かみと電子音の冷たさの対比を表現しています。'
    },
    {
      id: 3,
      title: 'Material Dialogue',
      duration: '5:15',
      thumbnail: 'https://readdy.ai/api/search-image?query=mixed%20media%20abstract%20art%20installation%20combining%20concrete%20and%20wood%20materials%20in%20dark%20atmospheric%20gallery%20contemporary%20sculpture%20minimalist%20composition&width=1200&height=675&seq=video3&orientation=landscape',
      description: '異なる素材同士の対話を音と映像で記録したドキュメンタリー作品。制作プロセスそのものがアートとなる瞬間を捉えています。'
    },
    {
      id: 4,
      title: 'Sound Structures',
      duration: '6:20',
      thumbnail: 'https://readdy.ai/api/search-image?query=abstract%20geometric%20sculpture%20installation%20with%20sound%20waves%20visualization%20dark%20moody%20atmosphere%20contemporary%20art%20minimalist%20design%20architectural%20forms&width=1200&height=675&seq=video4&orientation=landscape',
      description: '音の構造を視覚化し、彫刻作品として再構築する試み。聴覚と視覚の境界を曖昧にする実験的アプローチです。'
    }
  ];

  const sculptures = [
    {
      id: 1,
      title: '刻 / koku 002',
      material: 'Cherry, Concrete',
      year: '2025',
      description: '桜材とコンクリートを組み合わせた作品。有機的な木材の温かみと、無機質なコンクリートの冷たさが対比を生み出し、時間の経過と素材の変化を表現しています。',
      images: [
        'https://static.readdy.ai/image/9be18df7882b5fc5208ed7a2c3c10f73/082e6b0c5f4b4d5d2b9e9d598988af3d.jpeg',
        'https://readdy.ai/api/search-image?query=abstract%20concrete%20and%20cherry%20wood%20sculpture%20detail%20shot%20showing%20material%20texture%20contrast%20in%20dark%20minimalist%20gallery%20contemporary%20art%20close-up%20view%20natural%20grain%20and%20rough%20concrete%20surface&width=900&height=600&seq=sculpture1-2&orientation=landscape',
        'https://readdy.ai/api/search-image?query=abstract%20concrete%20and%20cherry%20wood%20sculpture%20different%20angle%20view%20in%20dark%20atmospheric%20gallery%20space%20contemporary%20minimalist%20art%20installation%20architectural%20forms&width=900&height=600&seq=sculpture1-3&orientation=landscape'
      ]
    },
    {
      id: 2,
      title: 'Organic Flow',
      material: 'Oak Wood',
      year: '2020',
      description: 'オーク材の自然な木目を活かした彫刻作品。素材が持つ有機的な流れを尊重しながら、新しい形態を探求しています。',
      images: [
        'https://readdy.ai/api/search-image?query=abstract%20wooden%20sculpture%20closeup%20showing%20natural%20grain%20texture%20and%20organic%20carved%20forms%20in%20dark%20studio%20setting%20warm%20atmospheric%20lighting%20contemporary%20minimalist%20aesthetic%20natural%20material&width=900&height=600&seq=sculpture2&orientation=landscape',
        'https://readdy.ai/api/search-image?query=oak%20wood%20abstract%20sculpture%20detail%20showing%20natural%20wood%20grain%20patterns%20and%20carved%20organic%20forms%20in%20dark%20minimalist%20setting%20contemporary%20art%20close-up%20texture&width=900&height=600&seq=sculpture2-2&orientation=landscape',
        'https://readdy.ai/api/search-image?query=oak%20wood%20abstract%20sculpture%20side%20view%20showing%20flowing%20organic%20forms%20in%20dark%20atmospheric%20gallery%20natural%20material%20contemporary%20minimalist%20art&width=900&height=600&seq=sculpture2-3&orientation=landscape'
      ]
    },
    {
      id: 3,
      title: 'Creation Process',
      material: 'Mixed Media',
      year: '2025',
      description: '制作プロセスそのものを記録した作品。素材と対話しながら形を生み出していく過程を、視覚的に表現しています。',
      images: [
        'https://static.readdy.ai/image/9be18df7882b5fc5208ed7a2c3c10f73/05211cfe1ee92e853ea0c1d11e51f417.jpeg',
        'https://readdy.ai/api/search-image?query=artist%20hands%20working%20on%20abstract%20sculpture%20in%20dark%20studio%20workspace%20with%20tools%20and%20materials%20creative%20process%20documentation%20atmospheric%20lighting%20contemporary%20art%20making%20craftsmanship&width=900&height=600&seq=sculpture3-2&orientation=landscape',
        'https://readdy.ai/api/search-image?query=mixed%20media%20abstract%20sculpture%20work%20in%20progress%20showing%20raw%20materials%20and%20creative%20process%20in%20dark%20studio%20setting%20contemporary%20art%20making%20documentation&width=900&height=600&seq=sculpture3-3&orientation=landscape'
      ]
    },
    {
      id: 4,
      title: 'Angular Void',
      material: 'Concrete',
      year: '2022',
      description: '幾何学的な形態とコンクリートの質感が生み出す、空間と余白の関係性を探求した作品です。',
      images: [
        'https://readdy.ai/api/search-image?query=geometric%20abstract%20concrete%20sculpture%20with%20angular%20forms%20in%20dark%20minimalist%20setting%20contemporary%20art%20monochromatic%20aesthetic%20architectural%20design%20clean%20lines&width=600&height=800&seq=sculpture4&orientation=portrait',
        'https://readdy.ai/api/search-image?query=geometric%20concrete%20sculpture%20detail%20showing%20angular%20forms%20and%20textured%20surface%20in%20dark%20atmospheric%20setting%20contemporary%20minimalist%20art%20close-up%20view&width=600&height=800&seq=sculpture4-2&orientation=portrait',
        'https://readdy.ai/api/search-image?query=geometric%20concrete%20sculpture%20alternative%20angle%20showing%20angular%20composition%20and%20void%20space%20in%20dark%20minimalist%20gallery%20contemporary%20architectural%20art&width=600&height=800&seq=sculpture4-3&orientation=portrait'
      ]
    },
    {
      id: 5,
      title: 'Curved Essence',
      material: 'Walnut Wood',
      year: '2018',
      description: 'ウォールナット材の持つ深い色合いと、曲線的なフォルムが調和した作品。素材の本質を引き出すことを目指しています。',
      images: [
        'https://readdy.ai/api/search-image?query=curved%20wooden%20abstract%20sculpture%20with%20smooth%20polished%20surface%20in%20dark%20atmospheric%20gallery%20natural%20wood%20grain%20contemporary%20minimalist%20art%20organic%20shapes&width=600&height=800&seq=sculpture5&orientation=portrait',
        'https://readdy.ai/api/search-image?query=walnut%20wood%20curved%20sculpture%20detail%20showing%20smooth%20polished%20surface%20and%20natural%20grain%20in%20dark%20minimalist%20setting%20contemporary%20art%20close-up%20organic%20forms&width=600&height=800&seq=sculpture5-2&orientation=portrait',
        'https://readdy.ai/api/search-image?query=walnut%20wood%20curved%20sculpture%20different%20angle%20showing%20organic%20flowing%20forms%20in%20dark%20atmospheric%20gallery%20contemporary%20minimalist%20art%20natural%20material&width=600&height=800&seq=sculpture5-3&orientation=portrait'
      ]
    },
    {
      id: 6,
      title: 'Material Fusion',
      material: 'Concrete, Wood',
      year: '2021',
      description: '異なる素材の融合により、新しい表現の可能性を探る実験的な作品です。',
      images: [
        'https://readdy.ai/api/search-image?query=mixed%20media%20abstract%20sculpture%20combining%20concrete%20and%20wood%20materials%20in%20dark%20minimalist%20gallery%20contemporary%20art%20textural%20contrast%20architectural%20forms&width=600&height=800&seq=sculpture6&orientation=portrait',
        'https://readdy.ai/api/search-image?query=concrete%20and%20wood%20mixed%20media%20sculpture%20detail%20showing%20material%20fusion%20and%20textural%20contrast%20in%20dark%20atmospheric%20setting%20contemporary%20art%20close-up&width=600&height=800&seq=sculpture6-2&orientation=portrait',
        'https://readdy.ai/api/search-image?query=concrete%20and%20wood%20abstract%20sculpture%20alternative%20view%20showing%20material%20combination%20in%20dark%20minimalist%20gallery%20contemporary%20architectural%20art&width=600&height=800&seq=sculpture6-3&orientation=portrait'
      ]
    }
  ];

  const handleSculptureClick = (id: number) => {
    navigate(`/sculpture/${id}`);
  };

  const handleUnderskinClick = () => {
    navigate('/underskin');
  };

  const handlePlayTrack = (trackUrl: string) => {
    setCurrentTrack(trackUrl);
    setShowPlayer(true);
  };

  const tracks = [
    {
      title: 'Voice that didn\'t speak',
      duration: '6:42',
      thumbnail: 'https://readdy.ai/api/search-image?query=abstract%20concrete%20texture%20with%20geometric%20patterns%20dark%20atmospheric%20background%20contemporary%20minimalist%20aesthetic%20monochromatic%20design%20architectural%20forms&width=800&height=800&seq=music-track1&orientation=squarish',
      url: 'https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/2135190345&color=%23000000&auto_play=true&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true'
    },
    {
      title: 'Wooden Whispers',
      duration: '5:28',
      thumbnail: 'https://readdy.ai/api/search-image?query=natural%20wood%20grain%20texture%20close-up%20with%20warm%20tones%20organic%20patterns%20contemporary%20minimalist%20aesthetic%20natural%20material&width=800&height=800&seq=music-track2&orientation=squarish',
      url: 'https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/2135190345&color=%23000000&auto_play=true&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true'
    },
    {
      title: 'Material Resonance',
      duration: '7:15',
      thumbnail: 'https://readdy.ai/api/search-image?query=mixed%20media%20abstract%20texture%20combining%20concrete%20and%20wood%20dark%20atmospheric%20contemporary%20minimalist%20aesthetic%20material%20contrast&width=800&height=800&seq=music-track3&orientation=squarish',
      url: 'https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/2135190345&color=%23000000&auto_play=true&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true'
    },
    {
      title: 'Ambient Structures',
      duration: '8:33',
      thumbnail: 'https://readdy.ai/api/search-image?query=abstract%20architectural%20forms%20with%20soft%20lighting%20minimalist%20composition%20contemporary%20aesthetic%20geometric%20shapes%20atmospheric%20mood&width=800&height=800&seq=music-track4&orientation=squarish',
      url: 'https://w.soundcloud.com/player/?url=https%3A//api.soundcloud.com/tracks/2135190345&color=%23000000&auto_play=true&hide_related=false&show_comments=true&show_user=true&show_reposts=false&show_teaser=true'
    }
  ];

  return (
    <div className="bg-[#1a1a1a] text-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/85 backdrop-blur-sm' : 'bg-transparent'}`}>
        <div className="px-4 md:px-8 py-4 md:py-6 flex items-center justify-between">
          <span className="text-lg md:text-xl font-light tracking-wider">Dighsk</span>
          
          {/* Desktop Menu */}
          <div className="hidden lg:flex items-center gap-8">
            <button onClick={() => scrollToSection('about')} className="text-sm font-light hover:text-[#e8dcc4] transition-colors relative group cursor-pointer whitespace-nowrap">
              About
              <span className="absolute bottom-0 left-0 w-0 h-px bg-[#e8dcc4] transition-all duration-300 group-hover:w-full"></span>
            </button>
            <button onClick={() => scrollToSection('sculptures')} className="text-sm font-light hover:text-[#e8dcc4] transition-colors relative group cursor-pointer whitespace-nowrap">
              Sculptures
              <span className="absolute bottom-0 left-0 w-0 h-px bg-[#e8dcc4] transition-all duration-300 group-hover:w-full"></span>
            </button>
            <button onClick={() => scrollToSection('music')} className="text-sm font-light hover:text-[#e8dcc4] transition-colors relative group cursor-pointer whitespace-nowrap">
              Music
              <span className="absolute bottom-0 left-0 w-0 h-px bg-[#e8dcc4] transition-all duration-300 group-hover:w-full"></span>
            </button>
            <button onClick={() => scrollToSection('video')} className="text-sm font-light hover:text-[#e8dcc4] transition-colors relative group cursor-pointer whitespace-nowrap">
              Video Works
              <span className="absolute bottom-0 left-0 w-0 h-px bg-[#e8dcc4] transition-all duration-300 group-hover:w-full"></span>
            </button>
            <button onClick={() => scrollToSection('design')} className="text-sm font-light hover:text-[#e8dcc4] transition-colors relative group cursor-pointer whitespace-nowrap">
              Design
              <span className="absolute bottom-0 left-0 w-0 h-px bg-[#e8dcc4] transition-all duration-300 group-hover:w-full"></span>
            </button>
            <button onClick={() => scrollToSection('contact')} className="text-sm font-light hover:text-[#e8dcc4] transition-colors relative group cursor-pointer whitespace-nowrap">
              Contact
              <span className="absolute bottom-0 left-0 w-0 h-px bg-[#e8dcc4] transition-all duration-300 group-hover:w-full"></span>
            </button>
            <div className="flex items-center gap-4 ml-4 pl-4 border-l border-gray-600">
              <a href="https://www.instagram.com/dighsk_/" target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center hover:text-[#e8dcc4] transition-colors cursor-pointer">
                <i className="ri-instagram-line text-lg"></i>
              </a>
              <a href="https://soundcloud.com/dighsk" target="_blank" rel="noopener noreferrer" className="w-8 h-8 flex items-center justify-center hover:text-[#e8dcc4] transition-colors cursor-pointer">
                <i className="ri-music-2-line text-lg"></i>
              </a>
            </div>
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          >
            <i className={`${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden bg-black/95 backdrop-blur-sm border-t border-gray-800">
            <div className="px-4 py-6 space-y-4">
              <button onClick={() => scrollToSection('about')} className="block w-full text-left py-2 text-sm font-light hover:text-[#e8dcc4] transition-colors cursor-pointer">
                About
              </button>
              <button onClick={() => scrollToSection('sculptures')} className="block w-full text-left py-2 text-sm font-light hover:text-[#e8dcc4] transition-colors cursor-pointer">
                Sculptures
              </button>
              <button onClick={() => scrollToSection('music')} className="block w-full text-left py-2 text-sm font-light hover:text-[#e8dcc4] transition-colors cursor-pointer">
                Music
              </button>
              <button onClick={() => scrollToSection('video')} className="block w-full text-left py-2 text-sm font-light hover:text-[#e8dcc4] transition-colors cursor-pointer">
                Video Works
              </button>
              <button onClick={() => scrollToSection('design')} className="block w-full text-left py-2 text-sm font-light hover:text-[#e8dcc4] transition-colors cursor-pointer">
                Design
              </button>
              <button onClick={() => scrollToSection('contact')} className="block w-full text-left py-2 text-sm font-light hover:text-[#e8dcc4] transition-colors cursor-pointer">
                Contact
              </button>
              <div className="flex items-center gap-4 pt-4 border-t border-gray-700">
                <a href="https://www.instagram.com/dighsk_/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center hover:text-[#e8dcc4] transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl"></i>
                </a>
                <a href="https://soundcloud.com/dighsk" target="_blank" rel="noopener noreferrer" className="w-10 h-10 flex items-center justify-center hover:text-[#e8dcc4] transition-colors cursor-pointer">
                  <i className="ri-music-2-line text-xl"></i>
                </a>
              </div>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://readdy.ai/api/search-image?query=dramatic%20abstract%20concrete%20sculpture%20in%20minimalist%20dark%20gallery%20space%20with%20dramatic%20side%20lighting%20contemporary%20art%20installation%20monochromatic%20aesthetic%20architectural%20forms%20textured%20surface%20atmospheric%20mood&width=1920&height=1080&seq=hero1&orientation=landscape"
            alt="Hero Background"
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        <div className="relative z-10 text-center w-full px-4 md:px-8 flex flex-col items-center">
          <h1 className="text-5xl md:text-8xl lg:text-[140px] font-bold tracking-[0.2em] mb-4 md:mb-6">DIGHSK</h1>
          <p className="text-xs md:text-base lg:text-lg font-light tracking-widest text-[#cccccc] max-w-[90%] md:max-w-[700px] lg:max-w-[900px]">Structural Artist / Crossing Boundaries Through Structure</p>
        </div>
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 animate-bounce">
          <div className="w-px h-16 bg-white/50"></div>
          <i className="ri-arrow-down-line text-2xl text-white/70"></i>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-16 md:py-32 px-4 md:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div>
              <h2 className="text-4xl md:text-6xl font-light mb-6 md:mb-8 text-[#e8dcc4]">About</h2>
              {/* Mobile version - short text */}
              <div className="lg:hidden">
                <p className="text-sm leading-relaxed mb-4 text-gray-300">
                  Dighskは、構造を媒介として<br />
                  彫刻・音楽・映像・空間を横断するStructural Artistです。<br />
                  素材の持つ本質的な美しさと構造的特性に着目した独自の表現を追求しています。
                </p>
                <p className="text-sm leading-relaxed text-gray-300">
                  Dighsk is a structural art project by Daisuke Abe.
                </p>
              </div>
              {/* Desktop version - full text */}
              <div className="hidden lg:block">
                <p className="text-base leading-relaxed mb-6 text-gray-300">
                  Dighskは、構造を媒介として彫刻、音楽、映像、空間を越境するStructural Artistです。木材とコンクリートを主な素材とした抽象彫刻を制作し、物質の持つ本質的な美しさと構造的特性に着目した独自の表現を追求しています。
                </p>
                <p className="text-base leading-relaxed mb-6 text-gray-300">
                  彫刻作品の制作と並行して、音楽制作や映像作品の制作にも取り組んでおり、アンビエント、エクスペリメンタル、ビートなどを中心に、幅広いジャンルを横断する作品を発表しています。これらすべての創作活動において、構造という概念が共通の軸となっています。
                </p>
                <p className="text-base leading-relaxed mb-6 text-gray-300">
                  本業ではインテリアデザイン、空間設計、家具設計を手がけており、これらの実践的なデザイン活動もまた、構造を通じた創作として認識しています。素材、音、映像、空間という異なるメディアを横断することで、構造の持つ可能性を多角的に探求し続けています。
                </p>
                <p className="text-base leading-relaxed text-gray-300">
                  Dighsk is a structural art project by Daisuke Abe.
                </p>
              </div>
            </div>
            <div className="flex justify-center">
              <div className="relative w-full max-w-[400px] h-[400px] md:h-[500px]">
                <img 
                  src="https://static.readdy.ai/image/9be18df7882b5fc5208ed7a2c3c10f73/5d5c0864df19c88ce50e6e10ed963f1d.jpeg"
                  alt="Artist Portrait"
                  className="w-full h-full object-cover object-top rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Sculptures Section */}
      <section id="sculptures" className="py-16 md:py-32 px-4 md:px-8 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row items-start gap-8 lg:gap-16">
            <h2 className="text-4xl md:text-6xl font-light text-[#e8dcc4] lg:writing-mode-vertical-rl flex-shrink-0">Sculptures</h2>
            <div className="flex-1 w-full">
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
                <div className="md:col-span-5 md:row-span-2 group cursor-pointer" onClick={() => handleSculptureClick(1)}>
                  <div className="relative h-[500px] md:h-[700px] overflow-hidden transition-all duration-500 hover:-translate-y-2">
                    <img 
                      src={sculptures[0].images[0]}
                      alt={sculptures[0].title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 backdrop-blur-sm p-2 md:p-3">
                      <h3 className="text-lg md:text-xl font-light mb-2">{sculptures[0].title}</h3>
                      <p className="text-xs md:text-sm text-gray-300">{sculptures[0].material} / {sculptures[0].year}</p>
                    </div>
                  </div>
                </div>
                <div className="md:col-span-7 group cursor-pointer" onClick={() => handleSculptureClick(2)}>
                  <div className="relative h-[280px] md:h-[340px] overflow-hidden transition-all duration-500 hover:-translate-y-2">
                    <img 
                      src={sculptures[1].images[0]}
                      alt={sculptures[1].title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 backdrop-blur-sm p-2 md:p-3">
                      <h3 className="text-lg md:text-xl font-light mb-2">{sculptures[1].title}</h3>
                      <p className="text-xs md:text-sm text-gray-300">{sculptures[1].material} / {sculptures[1].year}</p>
                    </div>
                  </div>
                </div>
                <div className="md:col-span-7 group cursor-pointer" onClick={() => handleSculptureClick(3)}>
                  <div className="relative h-[280px] md:h-[340px] overflow-hidden transition-all duration-500 hover:-translate-y-2">
                    <img 
                      src={sculptures[2].images[0]}
                      alt={sculptures[2].title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 backdrop-blur-sm p-2 md:p-3">
                      <h3 className="text-lg md:text-xl font-light mb-2">{sculptures[2].title}</h3>
                      <p className="text-xs md:text-sm text-gray-300">{sculptures[2].material} / {sculptures[2].year}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6 mt-4 md:mt-6">
                <div className="group cursor-pointer" onClick={() => handleSculptureClick(4)}>
                  <div className="relative h-[320px] overflow-hidden transition-all duration-500 hover:-translate-y-2">
                    <img 
                      src={sculptures[3].images[0]}
                      alt={sculptures[3].title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 backdrop-blur-sm p-2">
                      <h3 className="text-base md:text-lg font-light mb-1">{sculptures[3].title}</h3>
                      <p className="text-xs text-gray-300">{sculptures[3].material} / {sculptures[3].year}</p>
                    </div>
                  </div>
                </div>
                <div className="group cursor-pointer" onClick={() => handleSculptureClick(5)}>
                  <div className="relative h-[320px] overflow-hidden transition-all duration-500 hover:-translate-y-2">
                    <img 
                      src={sculptures[4].images[0]}
                      alt={sculptures[4].title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 backdrop-blur-sm p-2">
                      <h3 className="text-base md:text-lg font-light mb-1">{sculptures[4].title}</h3>
                      <p className="text-xs text-gray-300">{sculptures[4].material} / {sculptures[4].year}</p>
                    </div>
                  </div>
                </div>
                <div className="group cursor-pointer" onClick={() => handleSculptureClick(6)}>
                  <div className="relative h-[320px] overflow-hidden transition-all duration-500 hover:-translate-y-2">
                    <img 
                      src={sculptures[5].images[0]}
                      alt={sculptures[5].title}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 backdrop-blur-sm p-2">
                      <h3 className="text-base md:text-lg font-light mb-1">{sculptures[5].title}</h3>
                      <p className="text-xs text-gray-300">{sculptures[5].material} / {sculptures[5].year}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Music Section */}
      <section id="music" className="py-16 md:py-32 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 md:mb-16">
            <h2 className="text-4xl md:text-7xl font-light mb-4 md:mb-6">Music</h2>
            <p className="text-sm md:text-base leading-relaxed text-gray-300 max-w-2xl">
              アンビエント、エクスペリメンタル、ビートなどを中心に、素材の持つ音響特性を探求した作品を制作しています。彫刻制作のプロセスから得られる音や、素材そのものが持つ響きをサンプリングし、独自のサウンドスケープを構築しています。
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {tracks.map((track, index) => (
              <div key={index} className="group cursor-pointer" onClick={() => handlePlayTrack(track.url)}>
                <div className="relative h-[280px] md:h-[320px] rounded-lg overflow-hidden shadow-xl transition-all duration-500 hover:-translate-y-2">
                  <img 
                    src={track.thumbnail}
                    alt={track.title}
                    className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-sm border-2 border-white flex items-center justify-center group-hover:bg-white/30 transition-colors">
                      <i className="ri-play-fill text-3xl text-white ml-1"></i>
                    </div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-4">
                    <h3 className="text-base md:text-lg font-light mb-1">{track.title}</h3>
                    <p className="text-xs text-gray-400">{track.duration}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-8 md:mt-12 text-center">
            <a 
              href="https://soundcloud.com/dighsk" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-[#e8dcc4] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <span>Play on SoundCloud</span>
            </a>
          </div>
        </div>
      </section>

      {/* SoundCloud Player */}
      {showPlayer && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-sm border-t border-gray-800 p-4">
          <div className="max-w-7xl mx-auto flex items-center gap-4">
            <div className="flex-1">
              <iframe 
                width="100%" 
                height="100" 
                scrolling="no" 
                frameBorder="no" 
                allow="autoplay" 
                src={currentTrack}
                title="SoundCloud Player"
              ></iframe>
            </div>
            <button 
              onClick={() => setShowPlayer(false)}
              className="w-10 h-10 flex items-center justify-center hover:text-[#e8dcc4] transition-colors cursor-pointer flex-shrink-0"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>
      )}

      {/* Video Works Section */}
      <section id="video" className="py-16 md:py-32 px-4 md:px-8 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-light mb-12 md:mb-16 text-[#e8dcc4]">Video Works</h2>
          <div className="flex flex-col lg:flex-row gap-6 md:gap-8">
            <div className="flex-1">
              <div className="relative rounded-xl overflow-hidden shadow-2xl group">
                <div className="relative h-[300px] md:h-[600px]">
                  <img 
                    src={videos[currentVideoIndex].thumbnail}
                    alt={videos[currentVideoIndex].title}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/20 backdrop-blur-sm border-2 border-white flex items-center justify-center cursor-pointer hover:bg-white/30 transition-colors">
                      <i className="ri-play-fill text-4xl md:text-5xl text-white ml-2"></i>
                    </div>
                  </div>
                </div>
              </div>
              <div className="mt-6 md:mt-8">
                <h3 className="text-2xl md:text-3xl font-light mb-3 md:mb-4">{videos[currentVideoIndex].title}</h3>
                <p className="text-sm md:text-base leading-relaxed text-gray-300">{videos[currentVideoIndex].description}</p>
              </div>
            </div>
            <div className="w-full lg:w-96 space-y-4 md:space-y-6">
              {videos.map((video, index) => (
                <div 
                  key={video.id}
                  onClick={() => setCurrentVideoIndex(index)}
                  className={`relative rounded-lg overflow-hidden cursor-pointer transition-all duration-300 hover:shadow-xl ${currentVideoIndex === index ? 'ring-2 ring-[#e8dcc4]' : ''}`}
                >
                  <div className="relative h-24 md:h-32">
                    <img 
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className={`absolute inset-0 transition-colors ${currentVideoIndex === index ? 'bg-[#e8dcc4]/20' : 'bg-black/40 hover:bg-black/30'}`}></div>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/80 backdrop-blur-sm p-3 md:p-4">
                    <h4 className="text-xs md:text-sm font-light mb-1">{video.title}</h4>
                    <p className="text-xs text-gray-400">{video.duration}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Design Section */}
      <section id="design" className="py-16 md:py-32 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-light mb-6 md:mb-8 text-[#e8dcc4]">Design</h2>
          <p className="text-sm md:text-base leading-relaxed text-gray-300 mb-12 md:mb-16 max-w-3xl">
            インテリアデザイン、空間設計、家具設計を通じて、構造の持つ機能性と美しさを探求しています。彫刻作品と同様に、素材の本質を引き出し、空間に新しい価値を生み出すことを目指しています。
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            <div className="group cursor-pointer">
              <div className="relative h-[350px] md:h-[450px] rounded-lg overflow-hidden shadow-xl transition-all duration-500 hover:-translate-y-2">
                <img 
                  src="https://readdy.ai/api/search-image?query=minimalist%20interior%20design%20with%20concrete%20and%20wood%20materials%20dark%20atmospheric%20space%20contemporary%20furniture%20modern%20architecture%20clean%20lines%20structural%20elements%20natural%20lighting&width=800&height=1000&seq=design1&orientation=portrait"
                  alt="Interior Design"
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-4 md:p-6">
                  <h3 className="text-lg md:text-xl font-light mb-2">Interior Design</h3>
                  <p className="text-xs md:text-sm text-gray-300">構造を活かした空間デザイン</p>
                </div>
              </div>
            </div>
            <div className="group cursor-pointer">
              <div className="relative h-[350px] md:h-[450px] rounded-lg overflow-hidden shadow-xl transition-all duration-500 hover:-translate-y-2">
                <img 
                  src="https://readdy.ai/api/search-image?query=contemporary%20spatial%20design%20with%20geometric%20forms%20concrete%20walls%20and%20wooden%20elements%20minimalist%20architecture%20dark%20atmospheric%20lighting%20structural%20composition%20modern%20aesthetic&width=800&height=1000&seq=design2&orientation=portrait"
                  alt="Spatial Design"
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-4 md:p-6">
                  <h3 className="text-lg md:text-xl font-light mb-2">Spatial Design</h3>
                  <p className="text-xs md:text-sm text-gray-300">素材と構造による空間構成</p>
                </div>
              </div>
            </div>
            <div className="group cursor-pointer">
              <div className="relative h-[350px] md:h-[450px] rounded-lg overflow-hidden shadow-xl transition-all duration-500 hover:-translate-y-2">
                <img 
                  src="https://readdy.ai/api/search-image?query=minimalist%20furniture%20design%20with%20concrete%20and%20wood%20materials%20contemporary%20sculptural%20chair%20or%20table%20clean%20lines%20structural%20form%20modern%20craftsmanship%20dark%20background%20artistic%20photography&width=800&height=1000&seq=design3&orientation=portrait"
                  alt="Furniture Design"
                  className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black via-black/80 to-transparent p-4 md:p-6">
                  <h3 className="text-lg md:text-xl font-light mb-2">Furniture Design</h3>
                  <p className="text-xs md:text-sm text-gray-300">彫刻的アプローチによる家具</p>
                </div>
              </div>
            </div>
          </div>
          <div className="mt-12 md:mt-16 bg-[#0d0d0d] rounded-xl p-6 md:p-12">
            <h3 className="text-2xl md:text-3xl font-light mb-4 md:mb-6 text-[#e8dcc4]">構造を媒介とした創作</h3>
            <p className="text-sm md:text-base leading-relaxed text-gray-300 mb-4 md:mb-6">
              彫刻、音楽、映像、そしてデザイン。一見異なるこれらの表現領域は、すべて「構造」という概念によって結びついています。木材やコンクリートの物理的構造、音楽の時間的構造、映像の視覚的構造、空間の機能的構造。それぞれの媒体が持つ固有の構造を理解し、それを横断することで、新しい表現の可能性が生まれると考えています。
            </p>
            <p className="text-sm md:text-base leading-relaxed text-gray-300">
              本業のデザイン活動で培った空間や素材への理解は、アート作品の制作に深く影響を与えています。同時に、アート制作で探求する構造の本質は、デザイン活動にも還元されています。この相互作用こそが、私の創作活動の核心です。
            </p>
          </div>
        </div>
      </section>

      {/* Underskin Section */}
      <section id="underskin" className="py-12 md:py-20 px-4 md:px-8 bg-[#0d0d0d]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-6xl font-light mb-4 md:mb-5 text-[#e8dcc4]">Underskin</h2>
          <p className="text-sm md:text-base leading-relaxed text-gray-300 mb-8 md:mb-12 max-w-3xl">
            構造の露呈
          </p>
          <div className="relative overflow-x-auto pb-4">
            <div className="flex gap-4 md:gap-6">
              {[
                'https://static.readdy.ai/image/9be18df7882b5fc5208ed7a2c3c10f73/86badc646600e935e893555598d4387f.png',
                'https://static.readdy.ai/image/9be18df7882b5fc5208ed7a2c3c10f73/0988d67fddb398663300e7991413d135.jpeg',
                'https://static.readdy.ai/image/9be18df7882b5fc5208ed7a2c3c10f73/1b7bba567dec68d87eddd1b3819403a5.jpeg',
                'https://static.readdy.ai/image/9be18df7882b5fc5208ed7a2c3c10f73/9aa263754bff32ad9256696783a3a986.jpeg',
                'https://readdy.ai/api/search-image?query=abstract%20close-up%20photograph%20of%20weathered%20peeling%20wall%20texture%20with%20cracks%20and%20decay%20intense%20harsh%20lighting%20dissolving%20material%20quality%20minimalist%20composition%20monochromatic%20aesthetic%20architectural%20detail&width=300&height=300&seq=underskin5&orientation=squarish',
                'https://static.readdy.ai/image/9be18df7882b5fc5208ed7a2c3c10f73/4bb000250e22f13dd5c81c59cc39b09f.png',
                'https://static.readdy.ai/image/9be18df7882b5fc5208ed7a2c3c10f73/5318a36ed60c8426cf984cb73ee0dc73.jpeg',
                'https://static.readdy.ai/image/9be18df7882b5fc5208ed7a2c3c10f73/9bba694435d63c5168586ca228b2c80d.jpeg'
              ].map((src, index) => (
                <div 
                  key={index}
                  onClick={handleUnderskinClick}
                  className="flex-shrink-0 w-[300px] h-[300px] cursor-pointer group"
                >
                  <div className="relative w-full h-full overflow-hidden transition-all duration-500 hover:-translate-y-2">
                    <img 
                      src={src}
                      alt={`Underskin ${index + 1}`}
                      className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="mt-6 md:mt-8 text-center">
            <button 
              onClick={handleUnderskinClick}
              className="inline-flex items-center gap-2 text-sm text-[#e8dcc4] hover:text-white transition-colors cursor-pointer whitespace-nowrap"
            >
              <span>show all</span>
            </button>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 md:py-32 px-4 md:px-8 bg-[#0d0d0d]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-6xl font-light mb-8 md:mb-12 text-[#e8dcc4]">Contact</h2>
          <p className="text-sm md:text-base leading-relaxed text-gray-300 mb-12 md:mb-16">
            作品に関するお問い合わせ、展示のご依頼、コラボレーションのご相談など、お気軽にご連絡ください。
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 mb-12 md:mb-16">
            <div>
              <a href="mailto:contact@dighsk.com" className="block">
                <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 rounded-full bg-[#e8dcc4]/10 flex items-center justify-center hover:bg-[#e8dcc4]/20 transition-colors cursor-pointer">
                  <i className="ri-mail-line text-xl md:text-2xl text-[#e8dcc4]"></i>
                </div>
              </a>
              <h3 className="text-base md:text-lg font-light mb-2">Email</h3>
              <a href="mailto:contact@dighsk.com" className="text-xs md:text-sm text-gray-400 hover:text-[#e8dcc4] transition-colors cursor-pointer">contact@dighsk.com</a>
            </div>
            <div>
              <a href="https://www.instagram.com/dighsk_/" target="_blank" rel="noopener noreferrer" className="block">
                <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 rounded-full bg-[#e8dcc4]/10 flex items-center justify-center hover:bg-[#e8dcc4]/20 transition-colors cursor-pointer">
                  <i className="ri-instagram-line text-xl md:text-2xl text-[#e8dcc4]"></i>
                </div>
              </a>
              <h3 className="text-base md:text-lg font-light mb-2">Instagram</h3>
              <a href="https://www.instagram.com/dighsk_/" target="_blank" rel="noopener noreferrer" className="text-xs md:text-sm text-gray-400 hover:text-[#e8dcc4] transition-colors cursor-pointer">@dighsk_</a>
            </div>
            <div>
              <a href="https://soundcloud.com/dighsk" target="_blank" rel="noopener noreferrer" className="block">
                <div className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 rounded-full bg-[#e8dcc4]/10 flex items-center justify-center hover:bg-[#e8dcc4]/20 transition-colors cursor-pointer">
                  <i className="ri-music-2-line text-xl md:text-2xl text-[#e8dcc4]"></i>
                </div>
              </a>
              <h3 className="text-base md:text-lg font-light mb-2">SoundCloud</h3>
              <a href="https://soundcloud.com/dighsk" target="_blank" rel="noopener noreferrer" className="text-xs md:text-sm text-gray-400 hover:text-[#e8dcc4] transition-colors cursor-pointer">soundcloud.com/dighsk</a>
            </div>
          </div>
          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-4 md:space-y-6" data-readdy-form id="contact-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
              <input 
                type="text" 
                name="name"
                placeholder="お名前" 
                required
                className="bg-transparent border border-gray-600 rounded-lg px-4 md:px-6 py-3 md:py-4 text-sm focus:outline-none focus:border-[#e8dcc4] transition-colors"
              />
              <input 
                type="email" 
                name="email"
                placeholder="メールアドレス" 
                required
                className="bg-transparent border border-gray-600 rounded-lg px-4 md:px-6 py-3 md:py-4 text-sm focus:outline-none focus:border-[#e8dcc4] transition-colors"
              />
            </div>
            <input 
              type="text" 
              name="subject"
              placeholder="件名" 
              required
              className="w-full bg-transparent border border-gray-600 rounded-lg px-4 md:px-6 py-3 md:py-4 text-sm focus:outline-none focus:border-[#e8dcc4] transition-colors"
            />
            <textarea 
              name="message"
              placeholder="メッセージ" 
              rows={6}
              maxLength={500}
              required
              className="w-full bg-transparent border border-gray-600 rounded-lg px-4 md:px-6 py-3 md:py-4 text-sm focus:outline-none focus:border-[#e8dcc4] transition-colors resize-none"
            ></textarea>
            <button 
              type="submit" 
              disabled={formStatus === 'submitting'}
              className="w-full bg-[#e8dcc4] text-black font-light py-3 md:py-4 rounded-lg hover:bg-[#d4c8b0] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed text-sm md:text-base"
            >
              {formStatus === 'submitting' ? '送信中...' : formStatus === 'success' ? '送信完了!' : formStatus === 'error' ? '送信失敗' : '送信する'}
            </button>
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0a0a0a] py-12 md:py-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12 mb-8 md:mb-12">
            <div className="col-span-2 md:col-span-1">
              <div className="flex items-center gap-3 mb-4 md:mb-6">
                <span className="text-lg md:text-xl font-light tracking-wider">Dighsk</span>
              </div>
              <p className="text-xs md:text-sm text-gray-500 leading-relaxed">
                Structural Artist crossing boundaries through structure.
              </p>
            </div>
            <div>
              <h4 className="text-xs md:text-sm font-light mb-3 md:mb-4 text-[#e8dcc4]">Works</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('sculptures')} className="text-xs md:text-sm text-gray-400 hover:text-[#e8dcc4] transition-colors cursor-pointer">Sculptures</button></li>
                <li><button onClick={() => scrollToSection('music')} className="text-xs md:text-sm text-gray-400 hover:text-[#e8dcc4] transition-colors cursor-pointer">Music</button></li>
                <li><button onClick={() => scrollToSection('video')} className="text-xs md:text-sm text-gray-400 hover:text-[#e8dcc4] transition-colors cursor-pointer">Videos</button></li>
                <li><button onClick={() => scrollToSection('design')} className="text-xs md:text-sm text-gray-400 hover:text-[#e8dcc4] transition-colors cursor-pointer">Design</button></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs md:text-sm font-light mb-3 md:mb-4 text-[#e8dcc4]">About</h4>
              <ul className="space-y-2">
                <li><button onClick={() => scrollToSection('about')} className="text-xs md:text-sm text-gray-400 hover:text-[#e8dcc4] transition-colors cursor-pointer">Biography</button></li>
                <li><a href="#" className="text-xs md:text-sm text-gray-400 hover:text-[#e8dcc4] transition-colors cursor-pointer">Exhibitions</a></li>
                <li><a href="#" className="text-xs md:text-sm text-gray-400 hover:text-[#e8dcc4] transition-colors cursor-pointer">Press</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-xs md:text-sm font-light mb-3 md:mb-4 text-[#e8dcc4]">Connect</h4>
              <ul className="space-y-2">
                <li><a href="https://www.instagram.com/dighsk_/" target="_blank" rel="noopener noreferrer" className="text-xs md:text-sm text-gray-400 hover:text-[#e8dcc4] transition-colors cursor-pointer">Instagram</a></li>
                <li><a href="https://soundcloud.com/dighsk" target="_blank" rel="noopener noreferrer" className="text-xs md:text-sm text-gray-400 hover:text-[#e8dcc4] transition-colors cursor-pointer">SoundCloud</a></li>
                <li><a href="https://vimeo.com/dighsk" target="_blank" rel="noopener noreferrer" className="text-xs md:text-sm text-gray-400 hover:text-[#e8dcc4] transition-colors cursor-pointer">Vimeo</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-6 md:pt-8 flex flex-col md:flex-row items-center justify-between gap-6">
            <p className="text-xs md:text-sm text-gray-500 order-2 md:order-1">© 2025 Dighsk. All rights reserved.</p>
            <div className="flex items-center gap-4 md:gap-6 order-1 md:order-2">
              <a href="https://www.instagram.com/dighsk_/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg border border-gray-700 flex items-center justify-center hover:border-[#e8dcc4] transition-colors cursor-pointer">
                <i className="ri-instagram-line text-base md:text-lg"></i>
              </a>
              <a href="https://soundcloud.com/dighsk" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg border border-gray-700 flex items-center justify-center hover:border-[#e8dcc4] transition-colors cursor-pointer">
                <i className="ri-music-2-line text-base md:text-lg"></i>
              </a>
              <a href="https://vimeo.com/dighsk" target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg border border-gray-700 flex items-center justify-center hover:border-[#e8dcc4] transition-colors cursor-pointer">
                <i className="ri-video-line text-base md:text-lg"></i>
              </a>
              <a href="mailto:contact@dighsk.art" className="w-10 h-10 rounded-lg border border-gray-700 flex items-center justify-center hover:border-[#e8dcc4] transition-colors cursor-pointer">
                <i className="ri-mail-line text-base md:text-lg"></i>
              </a>
            </div>
            <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs md:text-sm text-gray-500 hover:text-[#e8dcc4] transition-colors cursor-pointer order-3">Powered by Readdy</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
